import { ApiProvider } from '@/provider/ApiProvider';
import { getActiveAssignment, getPreviewContextType } from '@/auth/activeAssignment';
import { isPartnerSideContext } from '@/utils/userRole';
import { getHeaderBrandId, getHeaderPropertyId } from '@/selection/headerSelection';

const countryListCache = new Map();
const countryListRequestCache = new Map();

/**
 * The cache key: the user, the ACTIVE role assignment (and a previewed context
 * type) plus the header page context, so a role switch or a preview never
 * reuses another role's country list (part 7, H3/F3).
 */
export function countryListCacheKey() {
  const assignment = getActiveAssignment();
  const userId = ApiProvider.returnUserData()?.id ?? '';
  const role = `${userId}:${assignment?.id ?? 'none'}:${getPreviewContextType() ?? ''}`;
  return isPartnerSideContext()
    ? `${role}|property_${getHeaderPropertyId() || ''}`
    : `${role}|brand_${getHeaderBrandId() || ''}`;
}

/**
 * Request flag of the activity / activation location pickers (part 7j, B17): activations are
 * not limited by country, so master_data answers ALL countries for this purpose instead of the
 * active scope's countries. Sent as `purpose` in the POST Countries body.
 */
export const ACTIVATION_LOCATION_PURPOSE = 'activation_location';

/**
 * Request flag of the global header country filter (owner 2026-10-12): the header offers every
 * country, because activation data is not limited by the user's countries. Contract data stays
 * limited by the server: a country outside the scope returns no contract rows.
 */
export const HEADER_FILTER_PURPOSE = 'header_filter';

/** The POST Countries body of the header country filter. */
export function headerCountryListBody() {
  const body = new FormData();
  body.append('page', '1');
  body.append('purpose', HEADER_FILTER_PURPOSE);
  return body;
}

/** Test helper. */
export function clearCountryListCache() {
  countryListCache.clear();
  countryListRequestCache.clear();
}

/**
 * The countries for a picker. Without a purpose: the active scope's countries (contract,
 * bonus, budget, ... pickers). With `{ purpose: ACTIVATION_LOCATION_PURPOSE }`: every country
 * (activity / activation location pickers); cached separately.
 */
export async function getCountryList({ purpose } = {}) {
  let body = new FormData();

  const cacheScope = purpose
    ? `${countryListCacheKey()}|purpose_${purpose}`
    : countryListCacheKey();

  if (countryListCache.has(cacheScope)) {
    return countryListCache.get(cacheScope);
  }

  if (countryListRequestCache.has(cacheScope)) {
    return countryListRequestCache.get(cacheScope);
  }

  // The header page context only (the server answers the scope's countries).
  const pageProperty = getHeaderPropertyId();
  const pageBrand = getHeaderBrandId();
  if (isPartnerSideContext()) {
    if (pageProperty) body.append('property_id', pageProperty);
  } else if (pageBrand) {
    body.append('brand_id', pageBrand);
  }
  if (purpose) body.append('purpose', purpose);

  const request = (async () => {
    const response = await ApiProvider.webservicePostToken('Countries', body, 'yes');
    if (response.success) {
      const countryList = response.data || [];
      countryListCache.set(cacheScope, countryList);
      return countryList;
    }

    return [];
  })();

  countryListRequestCache.set(cacheScope, request);

  try {
    return await request;
  } catch (error) {
    ApiProvider.globalToastMessage(error.message, 5000, 'error');
    throw error; // Propagate the error
  } finally {
    countryListRequestCache.delete(cacheScope);
  }
}
